import {
	InfiniteData,
	keepPreviousData,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query"
import {
	ErrorResponse,
	InfinitivePage,
	SuccessResponse,
	useHttpClient,
} from "api/api"
import {
	AddEscalationCommentPayload,
	AssignEscalationPayload,
	CreateEscalationPayload,
	EditEscalationCommentPayload,
	Escalation,
	EscalationActivityEntry,
	ReplaceEscalationObserversPayload,
	TransitionEscalationStatusPayload,
	UpdateEscalationObservationsPayload,
	UpdateEscalationScreeningsPayload,
} from "interface/escalation"
import { TimelineItem, TimelineScreening } from "interface/patientType"
import { MinimalUser } from "interface/user"
import { useInvalidateOnRealtimeEvent } from "modules/realtime/useInvalidateOnRealtimeEvent"
import { useMemo } from "react"
import { queryKeys as patientQueryKeys } from "services/patients"

export const queryKeys = {
	escalationDetail: "escalation-detail",
	createEscalation: "create-escalation",
	assignEscalation: "assign-escalation",
	escalationObservers: "escalation-observers",
	escalationStatus: "escalation-status",
	escalationObservations: "escalation-observations",
	escalationScreenings: "escalation-screenings",
	addComment: "escalation-add-comment",
	editComment: "escalation-edit-comment",
	deleteComment: "escalation-delete-comment",
	deleteEscalation: "delete-escalation",
	escalationCandidates: "escalation-candidates",
}

export const useEscalationDetails = (escalationId?: string) => {
	const { httpClient } = useHttpClient()
	const queryKey = useMemo(
		() => [queryKeys.escalationDetail, escalationId],
		[escalationId],
	)

	useInvalidateOnRealtimeEvent("escalation_updated", {
		queryKey,
		predicate: (data) => data?.escalationId === escalationId,
	})

	return useQuery({
		enabled: !!escalationId,
		placeholderData: keepPreviousData,
		queryKey,
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<Escalation>>(
				`/api/v1/escalations/${escalationId}`,
				{ signal },
			)
			return data
		},
	})
}

export const useCreateEscalation = (patientId?: string) => {
	const { httpClient } = useHttpClient()

	return useMutation<Escalation, ErrorResponse, CreateEscalationPayload>({
		mutationKey: [queryKeys.createEscalation],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.post<SuccessResponse<Escalation>>(
				`/api/v1/patients/${patientId}/escalations`,
				payload,
			)
			return data
		},
	})
}

export const useDeleteEscalation = (
	patientId: string,
	escalationId: string,
) => {
	const { httpClient } = useHttpClient()

	return useMutation<void, ErrorResponse>({
		mutationKey: [queryKeys.deleteEscalation, patientId, escalationId],
		mutationFn: async () => {
			await httpClient.delete(`/api/v1/escalations/${escalationId}`)
		},
	})
}

export const useAssignEscalation = (escalationId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<Escalation, ErrorResponse, AssignEscalationPayload>({
		mutationKey: [queryKeys.assignEscalation, escalationId],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.put<SuccessResponse<Escalation>>(
				`/api/v1/escalations/${escalationId}/assignee`,
				payload,
			)
			return data
		},
		onSuccess: (escalation) => {
			queryClient.setQueryData(
				[queryKeys.escalationDetail, escalation.id],
				escalation,
			)
		},
	})
}

export const useUpdateEscalationObservers = (escalationId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<
		Escalation,
		ErrorResponse,
		ReplaceEscalationObserversPayload
	>({
		mutationKey: [queryKeys.escalationObservers, escalationId],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.put<SuccessResponse<Escalation>>(
				`/api/v1/escalations/${escalationId}/observers`,
				payload,
			)
			return data
		},
		onSuccess: (escalation) => {
			queryClient.setQueryData(
				[queryKeys.escalationDetail, escalation.id],
				escalation,
			)
		},
	})
}

export const useUpdateEscalationStatus = (escalationId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<
		Escalation,
		ErrorResponse,
		TransitionEscalationStatusPayload
	>({
		mutationKey: [queryKeys.escalationStatus, escalationId],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.patch<SuccessResponse<Escalation>>(
				`/api/v1/escalations/${escalationId}/status`,
				payload,
			)
			return data
		},
		onSuccess: (escalation) => {
			queryClient.setQueryData(
				[queryKeys.escalationDetail, escalation.id],
				escalation,
			)
		},
	})
}

export const useUpdateEscalationObservations = (escalationId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<
		Escalation,
		ErrorResponse,
		UpdateEscalationObservationsPayload
	>({
		mutationKey: [queryKeys.escalationObservations, escalationId],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.patch<SuccessResponse<Escalation>>(
				`/api/v1/escalations/${escalationId}/observations`,
				payload,
			)
			return data
		},
		onSuccess: (escalation) => {
			queryClient.setQueryData(
				[queryKeys.escalationDetail, escalation.id],
				escalation,
			)
		},
	})
}

export const useUpdateEscalationScreenings = (escalationId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<
		Escalation,
		ErrorResponse,
		UpdateEscalationScreeningsPayload
	>({
		mutationKey: [queryKeys.escalationScreenings, escalationId],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.put<SuccessResponse<Escalation>>(
				`/api/v1/escalations/${escalationId}/linked-screenings`,
				payload,
			)
			return data
		},
		onSuccess: (escalation) => {
			const { id, patientId, linkedScreeningIds } = escalation

			if (linkedScreeningIds.length > 0) {
				const cachedById = new Map<string, TimelineScreening>()

				queryClient
					.getQueriesData<TimelineScreening[]>({
						queryKey: [patientQueryKeys.timelineScreenings, patientId],
					})
					.forEach(([, items]) => {
						items?.forEach((item) => cachedById.set(item.id, item))
					})

				queryClient
					.getQueriesData<InfiniteData<InfinitivePage<TimelineItem>>>({
						queryKey: [patientQueryKeys.timeline, patientId],
					})
					.forEach(([, infiniteData]) => {
						infiniteData?.pages.forEach((page) => {
							page.data.forEach((item) => {
								if (item.itemType === "screening") {
									cachedById.set(item.data.id, item.data)
								}
							})
						})
					})

				const screenings = linkedScreeningIds
					.map((screeningId) => cachedById.get(screeningId))
					.filter((screening): screening is TimelineScreening => !!screening)

				if (screenings.length === linkedScreeningIds.length) {
					queryClient.setQueryData(
						[
							patientQueryKeys.timelineScreenings,
							patientId,
							linkedScreeningIds,
						],
						screenings,
					)
				}
			}

			queryClient.setQueryData([queryKeys.escalationDetail, id], escalation)
		},
	})
}

export const useAddEscalationComment = (escalationId: string) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<
		EscalationActivityEntry,
		ErrorResponse,
		AddEscalationCommentPayload
	>({
		mutationKey: [queryKeys.addComment, escalationId],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.post<SuccessResponse<EscalationActivityEntry>>(
				`/api/v1/escalations/${escalationId}/comments`,
				payload,
			)
			return data
		},
		onSuccess: (entry) => {
			queryClient.setQueryData<Escalation>(
				[queryKeys.escalationDetail, escalationId],
				(prev) =>
					prev ? { ...prev, activity: [...prev.activity, entry] } : undefined,
			)
		},
	})
}

export const useEditEscalationComment = (
	escalationId: string,
	commentId: string,
) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation<
		EscalationActivityEntry,
		ErrorResponse,
		EditEscalationCommentPayload
	>({
		mutationKey: [queryKeys.editComment, escalationId, commentId],
		mutationFn: async (payload) => {
			const {
				data: { data },
			} = await httpClient.patch<SuccessResponse<EscalationActivityEntry>>(
				`/api/v1/escalations/${escalationId}/comments/${commentId}`,
				payload,
			)
			return data
		},
		onSuccess: (entry) => {
			queryClient.setQueryData<Escalation>(
				[queryKeys.escalationDetail, escalationId],
				(prev) => {
					if (!prev) {
						return undefined
					}
					return {
						...prev,
						activity: prev.activity.map((prevEntry) =>
							prevEntry.id === entry.id ? entry : prevEntry,
						),
					}
				},
			)
		},
	})
}

export const useDeleteEscalationComment = (
	escalationId: string,
	commentId: string,
) => {
	const { httpClient } = useHttpClient()
	const queryClient = useQueryClient()

	return useMutation({
		mutationKey: [queryKeys.deleteComment, escalationId, commentId],
		mutationFn: async () => {
			await httpClient.delete(
				`/api/v1/escalations/${escalationId}/comments/${commentId}`,
			)
		},
		onSuccess: () => {
			queryClient.setQueryData<Escalation>(
				[queryKeys.escalationDetail, escalationId],
				(prev) => {
					if (!prev) {
						return undefined
					}
					return {
						...prev,
						activity: prev.activity.filter(
							(prevEntry) => prevEntry.id !== commentId,
						),
					}
				},
			)
		},
	})
}

export const useEscalationCandidates = (patientId: string) => {
	const { httpClient } = useHttpClient()

	return useQuery({
		queryKey: [queryKeys.escalationCandidates, patientId],
		staleTime: 300_000,
		queryFn: async ({ signal }) => {
			const {
				data: { data },
			} = await httpClient.get<SuccessResponse<MinimalUser[]>>(
				`/api/v1/patients/${patientId}/escalation-candidates`,
				{ signal },
			)
			return data
		},
	})
}
